import { Suspense } from 'react';
import { Outlet, useNavigation } from 'react-router-dom';
import { NavigationProgress } from '@/components/feedback/NavigationProgress';
import { PageLoader } from '@/components/feedback/PageLoader';
import { STORAGE_KEYS } from '@/constants';
import { useIsMobile, useLocalStorage } from '@/hooks';
import { cn } from '@/lib/utils';
// Deep import: the displays barrel would pull its pages into the shell chunk.
import { useDisplayStatusRealtime } from '@/features/displays/hooks/useDisplayStatusRealtime';
import { SeatSyncBanner } from '@/features/onprem/components/SeatSyncBanner';
import { AppHeader } from './AppHeader';
import { AppSidebar } from './AppSidebar';
import { MobileRouteGate } from './MobileRouteGate';
import { MobileTabBar } from './MobileTabBar';

/**
 * Authenticated application shell. Desktop: sidebar + header + content.
 * Mobile (below `lg`): header + content + bottom tab bar, limited to
 * Displays, Buildings and Media.
 */
export function AppLayout() {
	const isMobile = useIsMobile();
	const { value: isNavCollapsed, setValue: setIsNavCollapsed } =
		useLocalStorage(STORAGE_KEYS.sidebarCollapsed, false);
	// Connection and "on air" of displays stay live on every screen.
	useDisplayStatusRealtime();
	const navigating = useNavigation().state === 'loading';

	const content = (
		<Suspense fallback={<PageLoader />}>
			<Outlet />
		</Suspense>
	);

	return (
		<div className="bg-muted/40 flex min-h-screen pr-[env(safe-area-inset-right)] pl-[env(safe-area-inset-left)]">
			{!isMobile && (
				<AppSidebar
					className="sticky top-0 h-screen"
					collapsed={isNavCollapsed}
					onToggleCollapsed={() => setIsNavCollapsed((previous) => !previous)}
				/>
			)}

			<div className="flex min-w-0 flex-1 flex-col">
				<AppHeader />
				<main
					aria-busy={navigating || undefined}
					className={cn(
						'relative flex-1 p-4 sm:p-6',
						// Room for the tab bar (h-16) and the home indicator.
						isMobile && 'pb-[calc(5rem+env(safe-area-inset-bottom))]',
					)}
				>
					<NavigationProgress />
					<div className="mx-auto w-full max-w-[1440px]">
						{/* On-prem installs only, while the seat sync needs attention. */}
						<SeatSyncBanner />
						{isMobile ? <MobileRouteGate>{content}</MobileRouteGate> : content}
					</div>
				</main>
			</div>

			{isMobile && <MobileTabBar />}
		</div>
	);
}
